import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import goBack from "../../../../../../assets/backIcon.svg";
import arrowRight from "../../../../../../assets/arrowRightWhite.svg";
import {
  STAKING_CAP_AMOUNT,
  stakingOption,
} from "../../../../../../common/constants/staking.constants";
import Fees from "../../../../../Fees/Fees";
import FormButtons from "../../../../../FormButtons/FormButtons";
import Modal from "../../../../../Modal/Modal";
import StakingOptionCard from "../../StakingOptionCard/StakingOptionCard";
import "./DepositStakingModal.scss";
import { IDetailNftData } from "../../../../../../common/interfaces/common.interface";
import {
  EMPTY_STRING,
  MESSAGE_TYPE,
  UNQ_PER_AMOUNT,
} from "../../../../../../common/constants/common.constants";
import { useAnchorWallet } from "@solana/wallet-adapter-react";
import { createNotification } from "../../../../../../utilities/notification";
import SkeletonItem from "../../../../../SkeletonItem/SkeletonItem";
import {
  Rarity,
  StakingType,
} from "../../../../../../common/enums/staking.enum";
import { AccountMeta, PublicKey } from "@solana/web3.js";
import {
  getUnqLevel1001Nfts,
  getUnqTokensAccountByMember,
} from "../../../../../../solana/common/helpers/staking.helpers";
import {
  IMemberStakingInfo,
  IStakingAccountInfo,
} from "../../../../../../common/interfaces/staking.interface";
import {
  DEPOSIT_STAKING_NFT_FEE,
  DEPOSIT_STAKING_TOKENS_FEE,
} from "../../../../../../common/constants/fees";
import { round } from "../../../../../../utilities/helpers";
import DepositNfts from "./DepositNfts/DepositNfts";
import DepositFts from "./DepositFts/DepositFts";
import { getInfoForStakingAccount } from "../../../../../../solana/methods/staking";

const DepositStakingModal: FC<{
  onClose: () => void;
  stakingAccount: IStakingAccountInfo;
  membersStakingInfo: IMemberStakingInfo | undefined;
  stakeUnqTokens: (tokenAccount: PublicKey, selectedAmount: number) => void;
  stakeNft: (remainingAccounts: AccountMeta[]) => void;
  depositing: boolean;
  hasDepositedNft?: boolean;
}> = ({
  onClose,
  stakingAccount,
  membersStakingInfo,
  stakeUnqTokens,
  stakeNft,
  hasDepositedNft,
  depositing,
}) => {
  const [activeStakingItem, setActiveStakingItem] = useState(stakingOption[0]);
  const [chosenNft, setChosenNft] = useState<IDetailNftData>();
  const [unqNfts, setUnqNfts] = useState<IDetailNftData[]>([]);
  const [loadingNfts, toggleLoadingNfts] = useState(true);
  const [wrongDepositAmount, setWrongDepositAmount] = useState<string>();

  const wallet = useAnchorWallet();

  const choseStakingOptionHandler = (
    isDisabled: boolean,
    title: string | number
  ) => {
    if (!isDisabled) {
      setActiveStakingItem(stakingOption.find((item) => item.title === title)!);
    }
  };
  const [amount, setAmount] = useState<number>();
  const [tokenAccount, setTokenAccount] = useState<PublicKey>();
  const [selectedAmount, setSelectedAmount] = useState<number | string>();
  const [availableRarities, setAvailableRarities] = useState<Rarity[]>();

  useEffect(() => {
    updateAvailableRarities();
  }, []);

  useEffect(() => {
    if (activeStakingItem.type === StakingType.UnqNFT && availableRarities) {
      void getNftsForWallet();
    }
    if (activeStakingItem.type === StakingType.UnqToken) {
      void getTokenAccounts();
    }
  }, [activeStakingItem, availableRarities]);

  const getNftsForWallet = useCallback(async () => {
    try {
      toggleLoadingNfts(true);
      const nfts = await getUnqLevel1001Nfts(wallet!);
      availableRarities &&
        setUnqNfts(
          nfts.filter((item) =>
            availableRarities.includes(item.universeRarity!)
          )
        );
      toggleLoadingNfts(false);
    } catch (error) {
      createNotification(
        MESSAGE_TYPE.ERROR,
        "Problem loading unq nfts from wallet"
      );
    }
  }, [wallet, availableRarities]);

  const getTokenAccounts = useCallback(async () => {
    try {
      const tokenAccount = await getUnqTokensAccountByMember(wallet!);
      if (tokenAccount === undefined || tokenAccount.amount === 0) {
        setWrongDepositAmount(
          "No UNQ tokens. Please deposit some to your wallet first"
        );
      } else {
        setAmount(tokenAccount.amount);
        setTokenAccount(tokenAccount.tokenAccountAddress);
      }
    } catch (error: any) {
      setWrongDepositAmount(
        "No UNQ tokens. Please deposit some to your wallet first"
      );
    }
  }, [wallet]);

  const renderStakingOptions = useMemo(() => {
    return stakingOption.map((option) => {
      return (
        <StakingOptionCard
          isActive={option.title === activeStakingItem.title}
          isDisabled={option.disabled}
          onChooseStakingPeriod={choseStakingOptionHandler}
          title={option.title}
          isDepositModal
          key={option.title}
        />
      );
    });
  }, [stakingOption, activeStakingItem]);

  const maxAvailableDepositAmount = useMemo(() => {
    return round(
      (STAKING_CAP_AMOUNT * UNQ_PER_AMOUNT -
        stakingAccount.raisedAmount * UNQ_PER_AMOUNT) /
        UNQ_PER_AMOUNT,
      6
    );
  }, [stakingAccount.raisedAmount]);

  const updateDepositAmount = (value) => {
    const maxUserAvailableAmount =
      amount && amount > maxAvailableDepositAmount
        ? maxAvailableDepositAmount
        : amount;
    if (value === EMPTY_STRING) {
      setWrongDepositAmount("Amount of tokens can not be empty");
    }
    if (value !== EMPTY_STRING && value <= 0) {
      setWrongDepositAmount(
        "Amount of tokens can not be zero or less then zero"
      );
    }

    if (
      value !== EMPTY_STRING &&
      maxUserAvailableAmount &&
      value > maxUserAvailableAmount
    ) {
      setWrongDepositAmount(
        "Amount of tokens can not be higher of available amount "
      );
    }
    if (
      value > 0 &&
      value !== EMPTY_STRING &&
      maxUserAvailableAmount &&
      value <= maxUserAvailableAmount
    ) {
      setWrongDepositAmount(EMPTY_STRING);
    }
    setSelectedAmount(value);
  };

  const stakeTokens = useCallback(async () => {
    if (wallet) {
      if (activeStakingItem.type === StakingType.UnqNFT) {
        stakeNftFromModal();
      } else {
        if (tokenAccount && selectedAmount) {
          stakeUnqTokens(tokenAccount, +selectedAmount);
        }
      }
    }
  }, [tokenAccount, selectedAmount, activeStakingItem, chosenNft, wallet]);

  const stakeNftFromModal = useCallback(async () => {
    if (chosenNft && stakeNft) {
      const remainingAccounts: AccountMeta[] = [
        { pubkey: chosenNft.tokenAccount!, isSigner: false, isWritable: true },
        {
          pubkey: chosenNft.mint!,
          isSigner: false,
          isWritable: false,
        },
        {
          pubkey: chosenNft.universeMetaData!,
          isSigner: false,
          isWritable: false,
        },
      ];

      stakeNft(remainingAccounts);
    }
  }, [chosenNft, stakeNft]);

  const updateAvailableRarities = useCallback(async () => {
    const rarities: Rarity[] = [];
    const updatedStakingAccount = await getInfoForStakingAccount(
      stakingAccount.accountAddress
    );
    if (updatedStakingAccount.nftStakeRecord) {
      switch (updatedStakingAccount.nftStakeRecord.rarity) {
        case Rarity.Common: {
          rarities.push(Rarity.Epic, Rarity.Legendary, Rarity.Rare);
          break;
        }
        case Rarity.Rare: {
          rarities.push(Rarity.Legendary, Rarity.Epic);
          break;
        }
        case Rarity.Epic: {
          rarities.push(Rarity.Legendary);
          break;
        }
        case Rarity.Legendary: {
          break;
        }
      }
    } else {
      rarities.push(Rarity.Epic, Rarity.Legendary, Rarity.Rare, Rarity.Common);
    }

    setAvailableRarities(rarities);
  }, [stakingAccount]);

  const depositFee = useMemo(() => {
    switch (true) {
      case activeStakingItem.type === StakingType.UnqToken &&
        !membersStakingInfo:
        return DEPOSIT_STAKING_TOKENS_FEE;
      case activeStakingItem.type === StakingType.UnqNFT && !hasDepositedNft:
        return DEPOSIT_STAKING_NFT_FEE;
      default:
        return 0;
    }
  }, [membersStakingInfo, activeStakingItem, hasDepositedNft]);

  return (
    <Modal closeModal={onClose}>
      <div className="deposit-staking">
        <h2>Deposit</h2>
        <div className="deposit-staking__staking-item">
          <p className="deposit-staking__title">Choose Staking item</p>
          <div className="deposit-staking__staking-options">
            {renderStakingOptions}
          </div>
        </div>
        {activeStakingItem.type === StakingType.UnqToken && (
          <DepositFts
            amount={amount}
            maxAvailableDepositAmount={maxAvailableDepositAmount}
            selectedAmount={selectedAmount}
            updateDepositAmount={updateDepositAmount}
            wrongDepositAmount={wrongDepositAmount}
          />
        )}
        {activeStakingItem.type === StakingType.UnqNFT ? (
          !loadingNfts ? (
            <DepositNfts
              availableRarities={availableRarities}
              chosenNft={chosenNft}
              loadingNfts={loadingNfts}
              setChosenNft={setChosenNft}
              setWrongDepositAmount={setWrongDepositAmount}
              stakingAccount={stakingAccount}
              unqNfts={unqNfts}
            />
          ) : (
            <SkeletonItem height={60} borderRadius={8} />
          )
        ) : null}
        <Fees feesTitle="Deposit fee" feesDescription={`${depositFee} SOL`} />
      </div>
      <FormButtons
        cancelLabel="Go back"
        disabled={wrongDepositAmount !== EMPTY_STRING || depositing}
        onCancel={onClose}
        onSubmit={stakeTokens}
        submitLabel="Deposit"
        typeCancel="button"
        typeSubmit="button"
        submitIcon={arrowRight}
        cancelIcon={goBack}
        submitting={depositing}
      />
    </Modal>
  );
};

export default DepositStakingModal;
